import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import CasaSectionNav from '@/components/feature/CasaSectionNav';

// ── Types ──

interface HkTask {
  id: string;
  room_id: string;
  departing_booking_id: string | null;
  status: string;
  task_date: string;
  completed_at: string | null;
  assigned_staff_id: string | null;
  rooms: { id: string; name: string } | null;
  departing_bookings: {
    id: string;
    guest_display_name: string | null;
    departure_date: string;
    actual_check_out_at: string | null;
    operational_status: string | null;
  } | null;
  assigned_staff: { id: string; full_name: string } | null;
}

interface HkTaskItem {
  id: string;
  task_id: string;
  template_item_id: string | null;
  status: string;
  photo_path: string | null;
  comment: string | null;
  issue_found: boolean | null;
  completed_at: string | null;
  template: {
    id: string;
    item_name: string;
    category: string;
    is_required: boolean;
    is_conditional: boolean;
    condition_type: string | null;
    sort_order: number;
  } | null;
}

type CleaningFilter = 'all' | 'not_started' | 'in_progress' | 'completed_ready';

type ItemCategory = 'standard' | 'damage' | 'optional_photo' | 'conditional';

interface DamageDialogState {
  itemId: string;
  taskId: string;
  step: 'choose' | 'report';
}

interface PhotoUploadState {
  itemId: string;
  taskId: string;
}

const CLEANING_STATUS_LABELS: Record<string, string> = {
  not_started: '未着手',
  assigned: '担当決定',
  in_progress: '清掃中',
  paused: '一時停止',
  completed: '完了',
  waiting_inspection: '点検待ち',
  ready: '準備完了',
  blocked: '要確認',
  not_required: '清掃不要',
};

const STATUS_ORDER: Record<string, number> = {
  not_required: 0,
  ready: 1,
  completed: 2,
  waiting_inspection: 3,
  paused: 4,
  not_started: 5,
  assigned: 6,
  in_progress: 7,
  blocked: 8,
};

const C = {
  bg: '#F6F4EE',
  cardBg: '#FFFEFA',
  text: '#252824',
  muted: '#6B6B67',
  primary: '#234638',
  accent: '#A8843A',
  border: '#DED9CC',
  headerBg: '#F0EDE5',
  dash: '#9a9890',
  danger: '#A33A32',
};

const DAMAGE_ITEM_SORT_ORDER = 5;
const OPTIONAL_PHOTO_SORT_ORDERS = [7, 9, 12];
const CONDITIONAL_SORT_ORDERS = [14, 15];

// ── Helpers ──

function getTodayJST(): string {
  const fmt = new Intl.DateTimeFormat('ja-JP', { timeZone: 'Asia/Tokyo', year: 'numeric', month: '2-digit', day: '2-digit' });
  const parts = fmt.formatToParts(new Date());
  const y = parts.find((p) => p.type === 'year')?.value ?? '';
  const m = parts.find((p) => p.type === 'month')?.value ?? '';
  const d = parts.find((p) => p.type === 'day')?.value ?? '';
  return `${y}-${m}-${d}`;
}

function addDaysJST(dateStr: string, days: number): string {
  const [y, m, d] = dateStr.split('-').map(Number);
  const date = new Date(Date.UTC(y, m - 1, d, 12, 0, 0));
  date.setUTCDate(date.getUTCDate() + days);
  const yy = date.getUTCFullYear();
  const mm = String(date.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(date.getUTCDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
}

function formatDateJST(dateStr: string): string {
  const [y, m, d] = dateStr.split('-').map(Number);
  const date = new Date(Date.UTC(y, m - 1, d, 12, 0, 0));
  const weekdays = ['日', '月', '火', '水', '木', '金', '土'];
  const wd = weekdays[date.getUTCDay()];
  return `${y}年${m}月${d}日（${wd}）`;
}

function formatTimeJST(isoStr: string | null): string {
  if (!isoStr) return '—';
  const d = new Date(isoStr);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleTimeString('ja-JP', { timeZone: 'Asia/Tokyo', hour: '2-digit', minute: '2-digit', hour12: false });
}

function categorizeItem(item: HkTaskItem): ItemCategory {
  if (!item.template) return 'standard';
  if (item.template.sort_order === DAMAGE_ITEM_SORT_ORDER && item.template.category === 'Check for Damage') return 'damage';
  if (OPTIONAL_PHOTO_SORT_ORDERS.includes(item.template.sort_order)) return 'optional_photo';
  if (CONDITIONAL_SORT_ORDERS.includes(item.template.sort_order)) return 'conditional';
  return 'standard';
}

// ── Component ──

export default function CleaningPage() {
  const todayStr = getTodayJST();
  const [selectedDate, setSelectedDate] = useState(todayStr);
  const [statusFilter, setStatusFilter] = useState<CleaningFilter>('all');
  const [tasks, setTasks] = useState<HkTask[]>([]);
  const [taskItems, setTaskItems] = useState<Map<string, HkTaskItem[]>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const [actionIsError, setActionIsError] = useState(false);
  const [confirmReadyTaskId, setConfirmReadyTaskId] = useState<string | null>(null);
  const [expandedTaskId, setExpandedTaskId] = useState<string | null>(null);
  const [itemLoadingIds, setItemLoadingIds] = useState<Set<string>>(new Set());
  const [damageDialog, setDamageDialog] = useState<DamageDialogState | null>(null);
  const [photoUploadState, setPhotoUploadState] = useState<PhotoUploadState | null>(null);
  const [damagePhotoFile, setDamagePhotoFile] = useState<File | null>(null);
  const [damageComment, setDamageComment] = useState('');
  const [signedUrls, setSignedUrls] = useState<Map<string, string>>(new Map());
  const fileInputRef = useRef<HTMLInputElement>(null);
  const optionalFileInputRef = useRef<HTMLInputElement>(null);

  // ── Fetch tasks: only departures that actually checked out ──
  const fetchTasks = useCallback(async (dateStr: string) => {
    setLoading(true);
    setError(null);
    try {
      const { data: hkData, error: hkErr } = await supabase
        .from('housekeeping_tasks')
        .select('id, room_id, departing_booking_id, status, task_date, completed_at, assigned_staff_id, rooms!inner(id, name), departing_bookings:bookings!housekeeping_tasks_departing_booking_id_fkey(id, guest_display_name, departure_date, actual_check_out_at, operational_status)')
        .eq('task_date', dateStr)
        .not('departing_booking_id', 'is', null)
        .order('created_at', { ascending: false });

      if (hkErr) throw new Error(`清掃タスクの取得に失敗: ${hkErr.message}`);

      // Filter: only tasks where departing booking is checked_out with actual_check_out_at
      const rawTasks = (hkData ?? []) as unknown as HkTask[];
      const validTasks = rawTasks.filter((t) => {
        const b = t.departing_bookings;
        return b && b.operational_status === 'checked_out' && b.actual_check_out_at !== null;
      });

      // Get assigned staff names
      const staffIds = [...new Set(validTasks.filter((t) => t.assigned_staff_id).map((t) => t.assigned_staff_id!))];
      const staffMap = new Map<string, string>();
      if (staffIds.length > 0) {
        const { data: profiles } = await supabase.from('profiles').select('id, full_name').in('id', staffIds);
        if (profiles) profiles.forEach((p) => staffMap.set(p.id, p.full_name));
      }
      const tasksWithStaff = validTasks.map((t) => ({
        ...t,
        assigned_staff: t.assigned_staff_id ? { id: t.assigned_staff_id, full_name: staffMap.get(t.assigned_staff_id) || '担当者' } : null,
      }));

      setTasks(tasksWithStaff);

      // Fetch task items for all valid tasks
      const allTaskIds = validTasks.map((t) => t.id);
      if (allTaskIds.length > 0) {
        const { data: itemsData, error: itemsErr } = await supabase
          .from('housekeeping_task_items')
          .select('id, task_id, template_item_id, status, photo_path, comment, issue_found, completed_at, template:housekeeping_checklist_template_items!housekeeping_task_items_template_item_id_fkey(id, item_name, category, is_required, is_conditional, condition_type, sort_order)')
          .in('task_id', allTaskIds)
          .order('sort_order', { referencedTable: 'housekeeping_checklist_template_items', ascending: true });

        if (!itemsErr && itemsData) {
          const map = new Map<string, HkTaskItem[]>();
          (itemsData as unknown as HkTaskItem[]).forEach((item) => {
            const list = map.get(item.task_id) || [];
            list.push(item);
            map.set(item.task_id, list);
          });
          setTaskItems(map);

          // Generate signed URLs for photos
          const photoItems = (itemsData as unknown as HkTaskItem[]).filter((it) => it.photo_path);
          if (photoItems.length > 0) {
            const newUrls = new Map<string, string>();
            await Promise.all(photoItems.map(async (it) => {
              if (!it.photo_path) return;
              try {
                const { data } = await supabase.storage.from('housekeeping-evidence').createSignedUrl(it.photo_path, 3600);
                if (data?.signedUrl) newUrls.set(it.id, data.signedUrl);
              } catch { /* ignore */ }
            }));
            setSignedUrls(newUrls);
          }
        }
      }
    } catch (err: unknown) {
      if (err instanceof Error) setError(err.message);
      else setError('データの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTasks(selectedDate);
  }, [selectedDate, fetchTasks]);

  // ── Filtered & sorted tasks ──
  const filteredTasks = useMemo(() => {
    let result = [...tasks];
    if (statusFilter === 'not_started') {
      result = result.filter((t) => t.status === 'not_started' || t.status === 'assigned');
    } else if (statusFilter === 'in_progress') {
      result = result.filter((t) => t.status === 'in_progress' || t.status === 'paused' || t.status === 'waiting_inspection' || t.status === 'blocked');
    } else if (statusFilter === 'completed_ready') {
      result = result.filter((t) => t.status === 'completed' || t.status === 'ready' || t.status === 'not_required');
    }
    result.sort((a, b) => (STATUS_ORDER[a.status] ?? 9) - (STATUS_ORDER[b.status] ?? 9));
    return result;
  }, [tasks, statusFilter]);

  // ── Stats ──
  const stats = useMemo(() => {
    const total = tasks.length;
    const completed = tasks.filter((t) => t.status === 'completed' || t.status === 'ready' || t.status === 'not_required').length;
    const inProgress = tasks.filter((t) => t.status === 'in_progress').length;
    const pending = tasks.filter((t) => t.status === 'not_started' || t.status === 'assigned' || t.status === 'paused' || t.status === 'waiting_inspection').length;
    const blocked = tasks.filter((t) => t.status === 'blocked').length;
    return { total, completed, inProgress, pending, blocked };
  }, [tasks]);

  // ── Checklist progress (standard required only: is_required=true AND is_conditional=false) ──
  const getChecklistProgress = (taskId: string): { required: number; done: number; remaining: number; damageChecked: boolean } => {
    const items = taskItems.get(taskId) || [];
    const standardRequired = items.filter((it) => it.template?.is_required && !it.template?.is_conditional);
    const done = standardRequired.filter((it) => it.status === 'completed').length;
    const damageItem = items.find((it) => categorizeItem(it) === 'damage');
    const damageChecked = damageItem ? damageItem.issue_found !== null : false;
    return { required: standardRequired.length, done, remaining: standardRequired.length - done, damageChecked };
  };

  // ── Get signed URL helper ──
  const getSignedUrl = useCallback(async (photoPath: string): Promise<string | null> => {
    try {
      const { data } = await supabase.storage.from('housekeeping-evidence').createSignedUrl(photoPath, 3600);
      return data?.signedUrl ?? null;
    } catch {
      return null;
    }
  }, []);

  // ── Action: toggle checklist item (standard items only) ──
  const handleToggleItem = useCallback(async (itemId: string, currentlyCompleted: boolean, taskId: string) => {
    setItemLoadingIds((prev) => new Set(prev).add(itemId));
    try {
      const { data, error: rpcErr } = await supabase.rpc('set_housekeeping_task_item_completed', {
        p_task_item_id: itemId,
        p_completed: !currentlyCompleted,
      });
      if (rpcErr) {
        setActionIsError(true);
        setActionMessage(rpcErr.message || '項目の更新に失敗しました');
        return;
      }
      const result = data as Record<string, unknown> | null;
      if (!result?.success) {
        setActionIsError(true);
        const err = (result?.error as string) || '';
        if (err === 'use_damage_check_rpc') {
          setActionMessage('破損確認項目は専用の操作を使用してください。');
        } else if (err === 'task_not_editable') {
          setActionMessage('このタスクは編集できません。');
        } else if (err === 'task_blocked') {
          setActionMessage('このタスクはブロックされています。');
        } else {
          setActionMessage((result?.error as string) || '項目の更新に失敗しました');
        }
        return;
      }
      await fetchTasks(selectedDate);
    } catch {
      setActionIsError(true);
      setActionMessage('通信エラーが発生しました。');
    } finally {
      setItemLoadingIds((prev) => {
        const next = new Set(prev);
        next.delete(itemId);
        return next;
      });
    }
  }, [selectedDate, fetchTasks]);

  // ── Action: damage check (no damage) ──
  const handleDamageNoIssue = useCallback(async (itemId: string) => {
    setDamageDialog(null);
    setItemLoadingIds((prev) => new Set(prev).add(itemId));
    try {
      const { data, error: rpcErr } = await supabase.rpc('record_housekeeping_damage_check', {
        p_task_item_id: itemId,
        p_issue_found: false,
      });
      if (rpcErr) { setActionIsError(true); setActionMessage(rpcErr.message); return; }
      const result = data as Record<string, unknown> | null;
      if (!result?.success) { setActionIsError(true); setActionMessage((result?.error as string) || '操作に失敗しました'); return; }
      await fetchTasks(selectedDate);
    } catch {
      setActionIsError(true);
      setActionMessage('通信エラーが発生しました。');
    } finally {
      setItemLoadingIds((prev) => { const n = new Set(prev); n.delete(itemId); return n; });
    }
  }, [selectedDate, fetchTasks]);

  // ── Action: damage check (issue found - submit with photo+comment) ──
  const handleDamageSubmit = useCallback(async () => {
    if (!damageDialog) return;
    const itemId = damageDialog.itemId;
    setItemLoadingIds((prev) => new Set(prev).add(itemId));

    try {
      let photoPath: string | null = null;

      if (damagePhotoFile) {
        const taskItemsForTask = taskItems.get(damageDialog.taskId) || [];
        const uuid = crypto.randomUUID();
        const ext = damagePhotoFile.name.split('.').pop() || 'jpg';
        const storagePath = `housekeeping/${damageDialog.taskId}/${itemId}/${uuid}.${ext}`;
        const { error: uploadErr } = await supabase.storage.from('housekeeping-evidence').upload(storagePath, damagePhotoFile, {
          cacheControl: '3600',
          upsert: false,
        });
        if (uploadErr) {
          setActionIsError(true);
          setActionMessage(uploadErr.message || '写真のアップロードに失敗しました');
          setItemLoadingIds((prev) => { const n = new Set(prev); n.delete(itemId); return n; });
          return;
        }
        photoPath = storagePath;
      }

      if (!photoPath || !damageComment.trim()) {
        setActionIsError(true);
        setActionMessage('写真とコメントの両方が必要です。');
        setItemLoadingIds((prev) => { const n = new Set(prev); n.delete(itemId); return n; });
        return;
      }

      const { data, error: rpcErr } = await supabase.rpc('record_housekeeping_damage_check', {
        p_task_item_id: itemId,
        p_issue_found: true,
        p_photo_path: photoPath,
        p_comment: damageComment.trim(),
      });

      if (rpcErr) {
        setActionIsError(true);
        setActionMessage(rpcErr.message);
        return;
      }
      const result = data as Record<string, unknown> | null;
      if (!result?.success) {
        setActionIsError(true);
        setActionMessage((result?.error as string) || '操作に失敗しました');
        return;
      }

      setDamageDialog(null);
      setDamagePhotoFile(null);
      setDamageComment('');
      await fetchTasks(selectedDate);
    } catch {
      setActionIsError(true);
      setActionMessage('通信エラーが発生しました。');
    } finally {
      setItemLoadingIds((prev) => { const n = new Set(prev); n.delete(itemId); return n; });
    }
  }, [damageDialog, damagePhotoFile, damageComment, taskItems, selectedDate, fetchTasks]);

  // ── Action: upload optional photo ──
  const handleOptionalPhotoUpload = useCallback(async (file: File) => {
    if (!photoUploadState) return;
    const { itemId, taskId } = photoUploadState;
    setItemLoadingIds((prev) => new Set(prev).add(itemId));

    try {
      const uuid = crypto.randomUUID();
      const ext = file.name.split('.').pop() || 'jpg';
      const storagePath = `housekeeping/${taskId}/${itemId}/${uuid}.${ext}`;
      const { error: uploadErr } = await supabase.storage.from('housekeeping-evidence').upload(storagePath, file, {
        cacheControl: '3600',
        upsert: false,
      });
      if (uploadErr) {
        setActionIsError(true);
        setActionMessage(uploadErr.message || '写真のアップロードに失敗しました');
        return;
      }

      const { data, error: rpcErr } = await supabase.rpc('set_housekeeping_item_optional_photo', {
        p_task_item_id: itemId,
        p_photo_path: storagePath,
      });
      if (rpcErr) { setActionIsError(true); setActionMessage(rpcErr.message); return; }
      const result = data as Record<string, unknown> | null;
      if (!result?.success) { setActionIsError(true); setActionMessage((result?.error as string) || '操作に失敗しました'); return; }

      setPhotoUploadState(null);
      await fetchTasks(selectedDate);
    } catch {
      setActionIsError(true);
      setActionMessage('通信エラーが発生しました。');
    } finally {
      setItemLoadingIds((prev) => { const n = new Set(prev); n.delete(itemId); return n; });
    }
  }, [photoUploadState, selectedDate, fetchTasks]);

  // ── Action: mark task ready ──
  const handleMarkReady = useCallback(async (taskId: string) => {
    setConfirmReadyTaskId(null);
    setActionLoading(true);
    setActionMessage(null);
    setActionIsError(false);
    try {
      const { data, error: rpcErr } = await supabase.rpc('mark_housekeeping_task_ready', { p_task_id: taskId });
      if (rpcErr) {
        setActionIsError(true);
        setActionMessage(rpcErr.message || '清掃完了操作に失敗しました');
        return;
      }
      const result = data as Record<string, unknown> | null;
      if (result?.success) {
        setActionIsError(false);
        const roomName = tasks.find((t) => t.id === taskId)?.rooms?.name || '';
        setActionMessage(`${roomName}を準備完了にしました。`);
        await fetchTasks(selectedDate);
      } else {
        const err = (result?.error as string) || '不明なエラー';
        if (err === 'required_items_incomplete') {
          setActionIsError(true);
          setActionMessage(`必須チェックリストが完了していません（残り${result?.remaining_count || '?'}項目）`);
        } else if (err === 'damage_report_incomplete') {
          setActionIsError(true);
          setActionMessage((result?.reason as string) || '破損確認が完了していません。');
        } else {
          setActionIsError(true);
          setActionMessage(err || '清掃完了操作に失敗しました');
        }
      }
    } catch {
      setActionIsError(true);
      setActionMessage('通信エラーが発生しました。');
    } finally {
      setActionLoading(false);
    }
  }, [selectedDate, fetchTasks, tasks]);

  // ── Navigation ──
  const isToday = selectedDate === todayStr;

  const navBtnStyle = {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '4px',
    height: '36px', paddingLeft: '10px', paddingRight: '10px', borderRadius: '6px',
    fontSize: '13px', fontWeight: 500, color: C.text,
    backgroundColor: C.headerBg, border: 'none', cursor: 'pointer', whiteSpace: 'nowrap' as const,
  };
  const inputStyle = {
    height: '36px', paddingLeft: '10px', paddingRight: '10px', borderRadius: '6px',
    fontSize: '13px', color: C.text, backgroundColor: C.cardBg, border: `1px solid ${C.border}`, cursor: 'pointer',
  };

  // ── Render ──

  return (
    <div className="max-w-7xl">
      <CasaSectionNav />
      {/* Header */}
      <div className="mb-4">
        <h2 className="text-xl font-bold text-foreground-900 font-heading">清掃管理</h2>
        <p className="text-sm text-foreground-600 mt-1">チェックアウト済みの部屋のみ表示</p>
      </div>

      {/* Action message */}
      {actionMessage && (
        <div className="mb-3 px-3 py-2 rounded-md text-xs flex items-center gap-2"
          style={actionIsError ? { backgroundColor: '#FDF2F2', color: C.danger, border: '1px solid #F5C6C6' } : { backgroundColor: '#F0F7F4', color: C.primary, border: `1px solid ${C.border}` }}>
          <span className="w-4 h-4 flex items-center justify-center flex-shrink-0">
            <i className={actionIsError ? 'ri-error-warning-line text-sm' : 'ri-check-line text-sm'}></i>
          </span>
          <span className="flex-1">{actionMessage}</span>
          <button onClick={() => setActionMessage(null)} className="w-5 h-5 flex items-center justify-center rounded cursor-pointer flex-shrink-0" style={{ color: actionIsError ? C.danger : C.muted }}>
            <i className="ri-close-line text-sm"></i>
          </button>
        </div>
      )}

      {/* Stats bar */}
      {!loading && !error && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
          {[{ label: '全タスク', value: stats.total, color: C.text },
            { label: '未着手', value: stats.pending, color: C.accent },
            { label: '清掃中', value: stats.inProgress, color: '#4A7C6E' },
            { label: '完了', value: stats.completed, color: C.primary },
          ].map((s) => (
            <div key={s.label} className="rounded-lg border p-3" style={{ backgroundColor: C.cardBg, borderColor: C.border }}>
              <p className="text-2xl font-bold" style={{ color: s.color }}>{s.value}</p>
              <p className="text-xs mt-0.5" style={{ color: C.muted }}>{s.label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Date + Filter bar */}
      <div className="rounded-md border overflow-hidden mb-4" style={{ backgroundColor: C.bg, borderColor: C.border }}>
        <div className="px-3 py-3 flex flex-col sm:flex-row items-start sm:items-center gap-3" style={{ borderBottom: `1px solid ${C.border}` }}>
          <div className="flex items-center gap-2">
            <button onClick={() => setSelectedDate(addDaysJST(selectedDate, -1))} style={navBtnStyle} aria-label="前日">
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-left-s-line text-sm"></i></span>
            </button>
            <span className="text-sm font-bold whitespace-nowrap px-1" style={{ color: C.text }}>{formatDateJST(selectedDate)}</span>
            <button onClick={() => setSelectedDate(addDaysJST(selectedDate, 1))} style={navBtnStyle} aria-label="翌日">
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-s-line text-sm"></i></span>
            </button>
            <input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} style={inputStyle} aria-label="日付選択" />
            {!isToday && (
              <button onClick={() => setSelectedDate(todayStr)} className="px-3 py-2 rounded-md text-xs font-semibold cursor-pointer whitespace-nowrap" style={{ backgroundColor: C.primary, color: '#FFFEFA' }}>今日</button>
            )}
          </div>
          <div className="flex items-center gap-1.5">
            {([{ key: 'all', label: 'すべて' }, { key: 'not_started', label: '未着手' }, { key: 'in_progress', label: '清掃中' }, { key: 'completed_ready', label: '完了' }] as { key: CleaningFilter; label: string }[]).map((f) => (
              <button key={f.key} onClick={() => setStatusFilter(f.key)}
                className="px-3 py-1.5 rounded-full text-xs font-medium cursor-pointer whitespace-nowrap transition-colors"
                style={statusFilter === f.key ? { backgroundColor: C.primary, color: '#FFFEFA' } : { backgroundColor: C.headerBg, color: C.muted }}>
                {f.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <span className="w-5 h-5 flex items-center justify-center"><i className="ri-loader-4-line text-lg animate-spin" style={{ color: C.primary }}></i></span>
        </div>
      )}

      {/* Error */}
      {!loading && error && (
        <div className="flex flex-col items-center justify-center py-12 gap-2">
          <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: C.headerBg }}>
            <i className="ri-error-warning-line text-lg" style={{ color: C.danger }}></i>
          </div>
          <p className="text-sm font-medium" style={{ color: C.danger }}>データの取得に失敗しました</p>
          <p className="text-xs max-w-md text-center px-4" style={{ color: C.muted }}>{error}</p>
          <button onClick={() => fetchTasks(selectedDate)} className="flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium cursor-pointer whitespace-nowrap" style={{ backgroundColor: C.primary, color: '#FFFEFA' }}>
            <span className="w-4 h-4 flex items-center justify-center"><i className="ri-refresh-line text-sm"></i></span>再読込
          </button>
        </div>
      )}

      {/* Empty */}
      {!loading && !error && filteredTasks.length === 0 && (
        <div className="rounded-md border p-12 text-center" style={{ backgroundColor: C.cardBg, borderColor: C.border }}>
          <div className="w-14 h-14 mx-auto mb-3 rounded-2xl flex items-center justify-center" style={{ backgroundColor: C.headerBg }}>
            <i className="ri-check-double-line text-xl" style={{ color: C.muted }}></i>
          </div>
          <p className="text-sm font-medium" style={{ color: C.text }}>清掃タスクはありません</p>
          <p className="text-xs mt-1" style={{ color: C.muted }}>チェックアウト済みの部屋に清掃タスクはありません。</p>
        </div>
      )}

      {/* Task cards */}
      {!loading && !error && filteredTasks.length > 0 && (
        <div className="space-y-3">
          {filteredTasks.map((task) => {
            const checklist = taskItems.get(task.id) || [];
            const categories = [...new Set(checklist.map((it) => it.template?.category || '').filter(Boolean))];
            const { required, done, remaining, damageChecked } = getChecklistProgress(task.id);
            const isExpanded = expandedTaskId === task.id;
            const canMarkReady = remaining === 0 && damageChecked;
            const isTerminal = ['ready', 'completed', 'not_required', 'blocked'].includes(task.status);

            return (
              <div key={task.id} className="rounded-md border overflow-hidden" style={{ backgroundColor: C.cardBg, borderColor: C.border }}>
                {/* Card header */}
                <div className="px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-2" style={{ borderBottom: isExpanded ? `1px solid ${C.border}` : 'none' }}>
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: C.primary }}></span>
                    <span className="text-sm font-semibold whitespace-nowrap" style={{ color: C.text }}>{task.rooms?.name || '—'}</span>
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium whitespace-nowrap"
                      style={task.status === 'ready' || task.status === 'completed' ? { backgroundColor: C.primary, color: '#FFFEFA' }
                        : task.status === 'blocked' ? { backgroundColor: '#FDF2F2', color: C.danger }
                        : task.status === 'in_progress' ? { backgroundColor: '#4A7C6E', color: '#FFFEFA' }
                        : { backgroundColor: C.headerBg, color: C.muted }}>
                      {CLEANING_STATUS_LABELS[task.status] || task.status}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {task.departing_bookings?.guest_display_name && (
                      <span className="text-xs" style={{ color: C.muted }}>{task.departing_bookings.guest_display_name}</span>
                    )}
                    {task.departing_bookings?.actual_check_out_at && (
                      <span className="text-xs" style={{ color: C.muted }}>退室 {formatTimeJST(task.departing_bookings.actual_check_out_at)}</span>
                    )}
                    {task.assigned_staff && (
                      <span className="inline-flex items-center gap-1 text-xs" style={{ color: C.muted }}>
                        <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-user-line text-xs"></i></span>
                        {task.assigned_staff.full_name}
                      </span>
                    )}
                    <span className="text-xs font-medium whitespace-nowrap" style={{ color: remaining === 0 ? C.primary : C.accent }}>
                      {done}/{required}項目
                      {damageChecked === false && done === required && (
                        <span className="ml-1 text-[10px]" style={{ color: C.danger }}>破損未確認</span>
                      )}
                    </span>
                    {checklist.length > 0 && (
                      <button onClick={() => setExpandedTaskId(isExpanded ? null : task.id)}
                        className="w-7 h-7 flex items-center justify-center rounded cursor-pointer transition-colors"
                        style={{ backgroundColor: C.headerBg, color: C.muted }}>
                        <i className={isExpanded ? 'ri-arrow-up-s-line text-sm' : 'ri-arrow-down-s-line text-sm'}></i>
                      </button>
                    )}
                    {!isTerminal && required > 0 && (
                      canMarkReady ? (
                        <button onClick={() => setConfirmReadyTaskId(task.id)} disabled={actionLoading}
                          className="flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                          style={{ backgroundColor: C.primary, color: '#FFFEFA' }}>
                          <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-check-line text-xs"></i></span>
                          清掃完了・準備完了
                        </button>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium whitespace-nowrap" style={{ backgroundColor: C.headerBg, color: C.muted }}>
                          <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-information-line text-xs"></i></span>
                          残り {remaining} / {required}項目
                        </span>
                      )
                    )}
                  </div>
                </div>

                {/* Checklist expansion */}
                {isExpanded && checklist.length > 0 && (
                  <div className="px-4 py-3 space-y-3" style={{ backgroundColor: C.bg }}>
                    {categories.map((cat) => {
                      const catItems = checklist.filter((it) => it.template?.category === cat);
                      return (
                        <div key={cat}>
                          <p className="text-[10px] font-semibold uppercase tracking-wide mb-1.5" style={{ color: C.muted }}>{cat}</p>
                          <div className="space-y-1">
                            {catItems.map((item) => {
                              const itemCat = categorizeItem(item);
                              const isCompleted = item.status === 'completed';
                              const isItemLoading = itemLoadingIds.has(item.id);
                              const isItemDisabled = isTerminal;

                              return (
                                <div key={item.id} className="flex items-center gap-2 text-xs rounded px-1 py-0.5 -mx-1">
                                  {/* ---- CONDITIONAL ITEM ---- */}
                                  {itemCat === 'conditional' && (
                                    <>
                                      <span className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                                        {isCompleted
                                          ? <i className="ri-checkbox-circle-fill text-sm" style={{ color: C.primary }}></i>
                                          : <i className="ri-checkbox-blank-circle-line text-sm" style={{ color: C.dash }}></i>}
                                      </span>
                                      <span style={{ color: isCompleted ? C.text : C.muted }} className="flex-1">{item.template?.item_name || '項目'}</span>
                                      <span className="text-[10px] px-1.5 py-0.5 rounded flex-shrink-0" style={{ backgroundColor: '#E8E0C8', color: C.accent }}>
                                        {item.template?.condition_type === 'dog' ? '犬連れ時のみ' : '特別時のみ'}
                                      </span>
                                      {!isTerminal && (
                                        <button onClick={() => { if (!isItemLoading) handleToggleItem(item.id, isCompleted, task.id); }}
                                          disabled={isItemLoading || isItemDisabled}
                                          className="w-6 h-6 flex items-center justify-center rounded cursor-pointer disabled:cursor-not-allowed"
                                          style={{ backgroundColor: C.headerBg }}>
                                          {isItemLoading ? <i className="ri-loader-4-line text-xs animate-spin" style={{ color: C.muted }}></i>
                                            : <i className={isCompleted ? 'ri-arrow-go-back-line text-xs' : 'ri-check-line text-xs'} style={{ color: C.muted }}></i>}
                                        </button>
                                      )}
                                    </>
                                  )}

                                  {/* ---- DAMAGE CHECK ITEM ---- */}
                                  {itemCat === 'damage' && (
                                    <>
                                      <span className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                                        {isCompleted
                                          ? <i className="ri-checkbox-circle-fill text-sm" style={{ color: C.primary }}></i>
                                          : <i className="ri-alert-line text-sm" style={{ color: C.accent }}></i>}
                                      </span>
                                      <span style={{ color: isCompleted ? C.text : C.text }} className="flex-1">{item.template?.item_name || '項目'}</span>
                                      {item.issue_found === true && (
                                        <span className="text-[10px] px-1.5 py-0.5 rounded flex-shrink-0" style={{ backgroundColor: '#FDF2F2', color: C.danger }}>破損あり</span>
                                      )}
                                      {item.issue_found === false && (
                                        <span className="text-[10px] px-1.5 py-0.5 rounded flex-shrink-0" style={{ backgroundColor: '#F0F7F4', color: C.primary }}>破損なし</span>
                                      )}
                                      {!isCompleted && !isTerminal && !isItemLoading && (
                                        <div className="flex items-center gap-1">
                                          <button onClick={() => setDamageDialog({ itemId: item.id, taskId: task.id, step: 'choose' })}
                                            className="px-2 py-1 rounded text-[10px] font-medium cursor-pointer whitespace-nowrap"
                                            style={{ backgroundColor: '#F0F7F4', color: C.primary }}>破損なし</button>
                                          <button onClick={() => setDamageDialog({ itemId: item.id, taskId: task.id, step: 'report' })}
                                            className="px-2 py-1 rounded text-[10px] font-medium cursor-pointer whitespace-nowrap"
                                            style={{ backgroundColor: '#FDF2F2', color: C.danger }}>破損あり</button>
                                        </div>
                                      )}
                                      {isCompleted && item.photo_path && (
                                        <button onClick={async () => {
                                          const url = signedUrls.get(item.id) || await getSignedUrl(item.photo_path!);
                                          if (url) window.open(url, '_blank');
                                        }} className="w-6 h-6 flex items-center justify-center rounded cursor-pointer flex-shrink-0" style={{ backgroundColor: C.headerBg }} title="写真を見る">
                                          <i className="ri-image-line text-xs" style={{ color: C.primary }}></i>
                                        </button>
                                      )}
                                      {isItemLoading && <span className="w-4 h-4 flex items-center justify-center"><i className="ri-loader-4-line text-sm animate-spin" style={{ color: C.muted }}></i></span>}
                                    </>
                                  )}

                                  {/* ---- STANDARD ITEM ---- */}
                                  {itemCat === 'standard' && (
                                    <>
                                      <button onClick={() => { if (!isItemDisabled && !isItemLoading) handleToggleItem(item.id, isCompleted, task.id); }}
                                        disabled={isItemDisabled || isItemLoading}
                                        className="flex items-center gap-2 flex-1 min-w-0 text-left rounded cursor-pointer disabled:cursor-not-allowed">
                                        <span className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                                          {isItemLoading ? <i className="ri-loader-4-line text-sm animate-spin" style={{ color: C.muted }}></i>
                                            : isCompleted ? <i className="ri-checkbox-circle-fill text-sm" style={{ color: C.primary }}></i>
                                            : <i className="ri-checkbox-blank-circle-line text-sm" style={{ color: C.muted }}></i>}
                                        </span>
                                        <span style={{ color: isCompleted ? C.text : C.muted }}>{item.template?.item_name || '項目'}</span>
                                        <span className="text-[10px] px-1 rounded flex-shrink-0" style={{ backgroundColor: C.headerBg, color: C.muted }}>必須</span>
                                      </button>
                                    </>
                                  )}

                                  {/* ---- OPTIONAL PHOTO ITEM ---- */}
                                  {itemCat === 'optional_photo' && (
                                    <>
                                      <button onClick={() => { if (!isItemDisabled && !isItemLoading) handleToggleItem(item.id, isCompleted, task.id); }}
                                        disabled={isItemDisabled || isItemLoading}
                                        className="flex items-center gap-2 flex-1 min-w-0 text-left rounded cursor-pointer disabled:cursor-not-allowed">
                                        <span className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                                          {isItemLoading ? <i className="ri-loader-4-line text-sm animate-spin" style={{ color: C.muted }}></i>
                                            : isCompleted ? <i className="ri-checkbox-circle-fill text-sm" style={{ color: C.primary }}></i>
                                            : <i className="ri-checkbox-blank-circle-line text-sm" style={{ color: C.muted }}></i>}
                                        </span>
                                        <span style={{ color: isCompleted ? C.text : C.muted }}>{item.template?.item_name || '項目'}</span>
                                        <span className="text-[10px] px-1 rounded flex-shrink-0" style={{ backgroundColor: C.headerBg, color: C.muted }}>必須</span>
                                      </button>
                                      <span className="text-[10px] px-1.5 py-0.5 rounded flex-shrink-0 whitespace-nowrap" style={{ backgroundColor: '#F6F4EE', color: C.muted }}>写真任意</span>
                                      {item.photo_path ? (
                                        <button onClick={async () => {
                                          const url = signedUrls.get(item.id) || await getSignedUrl(item.photo_path!);
                                          if (url) window.open(url, '_blank');
                                        }} className="w-6 h-6 flex items-center justify-center rounded cursor-pointer flex-shrink-0" style={{ backgroundColor: C.headerBg }} title="写真を見る">
                                          <i className="ri-image-line text-xs" style={{ color: C.primary }}></i>
                                        </button>
                                      ) : (
                                        !isTerminal && (
                                          <button onClick={() => { setPhotoUploadState({ itemId: item.id, taskId: task.id }); optionalFileInputRef.current?.click(); }}
                                            className="w-6 h-6 flex items-center justify-center rounded cursor-pointer flex-shrink-0" style={{ backgroundColor: C.headerBg }} title="写真を追加">
                                            <i className="ri-camera-line text-xs" style={{ color: C.muted }}></i>
                                          </button>
                                        )
                                      )}
                                    </>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                    {!isTerminal && remaining > 0 && (
                      <p className="text-xs" style={{ color: C.accent }}>必須項目 残り{remaining}項目 — 全{required}項目完了+破損確認後に準備完了にできます</p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Hidden file input for optional photo */}
      <input ref={optionalFileInputRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) handleOptionalPhotoUpload(f); e.target.value = ''; }} />

      {/* ── Damage Dialog ── */}
      {damageDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.4)' }}>
          <div className="rounded-xl max-w-sm w-full p-6 space-y-4" style={{ backgroundColor: C.cardBg, boxShadow: '0 8px 32px rgba(0,0,0,0.15)' }}>
            <div className="flex items-start gap-3">
              <span className="w-8 h-8 flex items-center justify-center rounded-full flex-shrink-0" style={{ backgroundColor: C.headerBg }}>
                <i className="ri-alert-line text-lg" style={{ color: C.accent }}></i>
              </span>
              <div>
                <h3 className="text-sm font-semibold" style={{ color: C.text }}>破損確認</h3>
                <p className="text-sm mt-1" style={{ color: C.muted }}>Inspect walls, furniture, and fixtures — 破損はありましたか？</p>
              </div>
            </div>

            {/* No damage option */}
            <button onClick={() => handleDamageNoIssue(damageDialog.itemId)}
              disabled={itemLoadingIds.has(damageDialog.itemId)}
              className="w-full px-4 py-2.5 rounded-lg text-sm font-medium cursor-pointer whitespace-nowrap disabled:opacity-50 text-left"
              style={{ backgroundColor: '#F0F7F4', color: C.primary, border: `1px solid ${C.primary}` }}>
              <span className="w-3.5 h-3.5 inline-flex items-center justify-center mr-2"><i className="ri-check-line text-sm"></i></span>
              破損なし — そのまま完了
            </button>

            {/* Damage found option */}
            {damageDialog.step === 'choose' && (
              <button onClick={() => setDamageDialog({ ...damageDialog, step: 'report' })}
                className="w-full px-4 py-2.5 rounded-lg text-sm font-medium cursor-pointer whitespace-nowrap text-left"
                style={{ backgroundColor: '#FDF2F2', color: C.danger, border: `1px solid ${C.danger}` }}>
                <span className="w-3.5 h-3.5 inline-flex items-center justify-center mr-2"><i className="ri-error-warning-line text-sm"></i></span>
                破損あり — 写真・コメント必須
              </button>
            )}

            {/* Damage report form */}
            {damageDialog.step === 'report' && (
              <div className="space-y-3 pt-2" style={{ borderTop: `1px solid ${C.border}` }}>
                <div>
                  <label className="text-xs font-medium block mb-1" style={{ color: C.text }}>破損写真（必須）</label>
                  {damagePhotoFile ? (
                    <div className="flex items-center gap-2">
                      <span className="text-xs flex-1 truncate" style={{ color: C.primary }}>{damagePhotoFile.name}</span>
                      <button onClick={() => setDamagePhotoFile(null)} className="text-xs cursor-pointer" style={{ color: C.danger }}>削除</button>
                    </div>
                  ) : (
                    <button onClick={() => fileInputRef.current?.click()}
                      className="w-full px-3 py-2 rounded-md text-xs font-medium cursor-pointer text-center" style={{ backgroundColor: C.headerBg, color: C.muted }}>
                      <span className="w-3.5 h-3.5 inline-flex items-center justify-center mr-1"><i className="ri-camera-line text-xs"></i></span>
                      写真を選択...
                    </button>
                  )}
                  <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
                    onChange={(e) => { const f = e.target.files?.[0]; if (f) setDamagePhotoFile(f); e.target.value = ''; }} />
                </div>
                <div>
                  <label className="text-xs font-medium block mb-1" style={{ color: C.text }}>破損内容（必須）</label>
                  <textarea value={damageComment} onChange={(e) => setDamageComment(e.target.value)}
                    maxLength={500} rows={3}
                    className="w-full px-3 py-2 rounded-md text-xs resize-none"
                    style={{ backgroundColor: C.cardBg, border: `1px solid ${C.border}`, color: C.text }}
                    placeholder="破損箇所と状況を記入してください..." />
                </div>
                <button onClick={handleDamageSubmit}
                  disabled={itemLoadingIds.has(damageDialog.itemId) || !damagePhotoFile || !damageComment.trim()}
                  className="w-full px-4 py-2 rounded-lg text-sm font-medium cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1"
                  style={{ backgroundColor: C.danger, color: '#FFFEFA' }}>
                  {itemLoadingIds.has(damageDialog.itemId) ? (
                    <span className="w-4 h-4 flex items-center justify-center"><i className="ri-loader-4-line text-sm animate-spin"></i></span>
                  ) : (
                    <>破損報告を保存</>
                  )}
                </button>
              </div>
            )}

            <button onClick={() => { setDamageDialog(null); setDamagePhotoFile(null); setDamageComment(''); }}
              className="w-full px-4 py-2 rounded-lg text-sm font-medium cursor-pointer whitespace-nowrap" style={{ backgroundColor: C.headerBg, color: C.text }}>
              キャンセル
            </button>
          </div>
        </div>
      )}

      {/* ── Ready Confirmation Dialog ── */}
      {confirmReadyTaskId && (() => {
        const task = tasks.find((t) => t.id === confirmReadyTaskId);
        if (!task) return null;
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.4)' }}>
            <div className="rounded-xl max-w-sm w-full p-6 space-y-4" style={{ backgroundColor: C.cardBg, boxShadow: '0 8px 32px rgba(0,0,0,0.15)' }}>
              <div className="flex items-start gap-3">
                <span className="w-8 h-8 flex items-center justify-center rounded-full flex-shrink-0" style={{ backgroundColor: C.headerBg }}>
                  <i className="ri-question-line text-lg" style={{ color: C.accent }}></i>
                </span>
                <div>
                  <h3 className="text-sm font-semibold" style={{ color: C.text }}>清掃完了の確認</h3>
                  <p className="text-sm mt-1" style={{ color: C.muted }}>
                    {task.rooms?.name || 'この部屋'}の清掃を完了し、次のお客様を受け入れ可能な「準備完了」にします。
                  </p>
                </div>
              </div>
              <div className="flex items-center justify-end gap-2 pt-2">
                <button onClick={() => setConfirmReadyTaskId(null)} disabled={actionLoading}
                  className="px-4 py-2 rounded-lg text-sm font-medium cursor-pointer whitespace-nowrap disabled:opacity-50"
                  style={{ backgroundColor: C.headerBg, color: C.text }}>キャンセル</button>
                <button onClick={() => handleMarkReady(confirmReadyTaskId)} disabled={actionLoading}
                  className="px-4 py-2 rounded-lg text-sm font-medium cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                  style={{ backgroundColor: C.primary, color: '#FFFEFA' }}>
                  {actionLoading ? (
                    <span className="w-4 h-4 flex items-center justify-center"><i className="ri-loader-4-line text-sm animate-spin"></i></span>
                  ) : '準備完了にする'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
